import React, { useState, useRef } from "react";
import { BlockMath } from "react-katex";
import "katex/dist/katex.min.css";
import "katex/dist/contrib/mhchem.min.js";
import { 
  Plus, Trash2, Send, RotateCcw, FlaskConical, Atom, Dna, Zap
} from "lucide-react";
import { Button } from "@/components/ui/button";

const tabs = [
  { id: "chem", label: "Quimica", icon: FlaskConical },
  { id: "physics", label: "Fisica", icon: Zap },
  { id: "bio", label: "Biologia", icon: Dna },
  { id: "elements", label: "Elementos", icon: Atom },
];

const chemSymbols = [
  { label: "H\u2082O", latex: "\\ce{H2O}", category: "compounds" },
  { label: "CO\u2082", latex: "\\ce{CO2}", category: "compounds" },
  { label: "O\u2082", latex: "\\ce{O2}", category: "compounds" },
  { label: "H\u2082", latex: "\\ce{H2}", category: "compounds" },
  { label: "NaCl", latex: "\\ce{NaCl}", category: "compounds" },
  { label: "HCl", latex: "\\ce{HCl}", category: "compounds" },
  { label: "NaOH", latex: "\\ce{NaOH}", category: "compounds" },
  { label: "H\u2082SO\u2084", latex: "\\ce{H2SO4}", category: "compounds" },
  { label: "\u2192", latex: "->", category: "arrows" },
  { label: "\u21CC", latex: "<=>", category: "arrows" },
  { label: "\u2192[cat]", latex: "->[cat]", category: "arrows" },
  { label: "+", latex: " + ", category: "arrows" },
  { label: "(s)", latex: "(s)", category: "state" },
  { label: "(l)", latex: "(l)", category: "state" },
  { label: "(g)", latex: "(g)", category: "state" },
  { label: "(aq)", latex: "(aq)", category: "state" },
  { label: "\u2191", latex: "^", category: "state" },
  { label: "\u2193", latex: "v", category: "state" },
  { label: "e\u207B", latex: "e-", category: "ions" },
  { label: "\u207A", latex: "+", category: "ions" },
  { label: "\u207B", latex: "-", category: "ions" },
  { label: "2\u00B7", latex: "2", category: "coeff" },
  { label: "3\u00B7", latex: "3", category: "coeff" },
  { label: "4\u00B7", latex: "4", category: "coeff" },
  { label: "6\u00B7", latex: "6", category: "coeff" },
];

// Pre-computed subset filters (avoid re-filtering on every render)
const chemCompounds = chemSymbols.filter((s) => s.category === "compounds");
const chemReactionSymbols = chemSymbols.filter(
  (s) => s.category === "arrows" || s.category === "state" || s.category === "coeff",
);

const physicsSymbols = [
  { label: "F=ma", latex: "F = ma" },
  { label: "v=d/t", latex: "v = \\frac{d}{t}" },
  { label: "a=\u0394v/\u0394t", latex: "a = \\frac{\\Delta v}{\\Delta t}" },
  { label: "E\u2096", latex: "E_k = \\frac{1}{2}mv^2" },
  { label: "Ep", latex: "E_p = mgh" },
  { label: "W", latex: "W = F \\cdot d \\cdot \\cos\\theta" },
  { label: "Q", latex: "Q = mc\\Delta T" },
  { label: "PV=nRT", latex: "PV = nRT" },
  { label: "V=IR", latex: "V = IR" },
  { label: "P=VI", latex: "P = VI" },
  { label: "v=\u03BBf", latex: "v = \\lambda f" },
  { label: "F\u2096", latex: "F = k\\frac{q_1 q_2}{r^2}" },
  { label: "=", latex: "=" },
  { label: "+", latex: "+" },
  { label: "\u2212", latex: "-" },
  { label: "\u00D7", latex: "\\times" },
  { label: "\u00F7", latex: "\\div" },
  { label: "a/b", latex: "\\frac{}{}", cursor: -3 },
  { label: "x\u00B2", latex: "^{2}" },
  { label: "x\u207F", latex: "^{}", cursor: -1 },
  { label: "\u221A", latex: "\\sqrt{}", cursor: -1 },
  { label: "\u03B1", latex: "\\alpha" },
  { label: "\u03B2", latex: "\\beta" },
  { label: "\u0394", latex: "\\Delta" },
  { label: "\u03B8", latex: "\\theta" },
  { label: "\u03BB", latex: "\\lambda" },
  { label: "\u03C1", latex: "\\rho" },
  { label: "\u03C3", latex: "\\sigma" },
  { label: "\u03C9", latex: "\\omega" },
];

const bioSymbols = [
  { label: "Fotosintesis", latex: "\\ce{6CO2 + 6H2O ->[luz] C6H12O6 + 6O2}" },
  { label: "Respiracion", latex: "\\ce{C6H12O6 + 6O2 -> 6CO2 + 6H2O} + \\text{ATP}" },
  { label: "ATP", latex: "\\ce{ADP + P_i -> ATP}" },
  { label: "ADN", latex: "\\text{A-T, G-C}" },
  { label: "ARN", latex: "\\text{A-U, G-C}" },
  { label: "Glucosa", latex: "\\ce{C6H12O6}" },
  { label: "\u2192", latex: "\\rightarrow" },
  { label: "\u21C4", latex: "\\rightleftharpoons" },
  { label: "+", latex: "+" },
];

const periodicElements = [
  { symbol: "H", name: "Hidrogeno", z: 1 },
  { symbol: "He", name: "Helio", z: 2 },
  { symbol: "Li", name: "Litio", z: 3 },
  { symbol: "C", name: "Carbono", z: 6 },
  { symbol: "N", name: "Nitrogeno", z: 7 },
  { symbol: "O", name: "Oxigeno", z: 8 },
  { symbol: "F", name: "Fluor", z: 9 },
  { symbol: "Na", name: "Sodio", z: 11 },
  { symbol: "Mg", name: "Magnesio", z: 12 },
  { symbol: "Al", name: "Aluminio", z: 13 },
  { symbol: "Si", name: "Silicio", z: 14 },
  { symbol: "P", name: "Fosforo", z: 15 },
  { symbol: "S", name: "Azufre", z: 16 },
  { symbol: "Cl", name: "Cloro", z: 17 },
  { symbol: "K", name: "Potasio", z: 19 },
  { symbol: "Ca", name: "Calcio", z: 20 },
  { symbol: "Fe", name: "Hierro", z: 26 },
  { symbol: "Cu", name: "Cobre", z: 29 },
  { symbol: "Zn", name: "Zinc", z: 30 },
  { symbol: "Ag", name: "Plata", z: 47 },
  { symbol: "Au", name: "Oro", z: 79 },
  { symbol: "Pb", name: "Plomo", z: 82 },
];

export const ScienceWhiteboard = ({ onSubmit, subjectColor = "#14b8a6" }) => {
  const [steps, setSteps] = useState([{ id: 1, latex: "", label: "Paso 1", mode: "chem" }]);
  const [activeStep, setActiveStep] = useState(1);
  const [activeTab, setActiveTab] = useState("chem");
  const inputRefs = useRef({});

  const addStep = () => {
    const newStep = { id: steps.length + 1, latex: "", label: `Paso ${steps.length + 1}`, mode: activeTab };
    setSteps([...steps, newStep]);
    setActiveStep(newStep.id);
  };

  const updateStep = (id, latex) => {
    setSteps(steps.map(s => s.id === id ? { ...s, latex } : s));
  };

  const removeStep = (id) => {
    if (steps.length > 1) {
      const newSteps = steps.filter(s => s.id !== id).map((s, i) => ({ ...s, id: i + 1, label: `Paso ${i + 1}` }));
      setSteps(newSteps);
      setActiveStep(Math.min(activeStep, newSteps.length));
    }
  };

  const insertAtCursor = (text) => {
    const currentStep = steps.find(s => s.id === activeStep);
    if (!currentStep) return;
    const input = inputRefs.current[activeStep];
    if (input) {
      const start = input.selectionStart;
      const end = input.selectionEnd;
      const newLatex = currentStep.latex.substring(0, start) + text + currentStep.latex.substring(end);
      updateStep(activeStep, newLatex);
      const cursor = typeof text === "object" ? text.cursor : undefined;
      setTimeout(() => {
        const newPos = start + (typeof text === "string" ? text.length : text.latex.length) + (cursor || 0);
        input.focus();
        input.setSelectionRange(newPos, newPos);
      }, 0);
    } else {
      updateStep(activeStep, currentStep.latex + (typeof text === "string" ? text : text.latex));
    }
  };

  const insertSymbol = (sym) => {
    const input = inputRefs.current[activeStep];
    const currentStep = steps.find(s => s.id === activeStep);
    if (!currentStep) return;
    if (input) {
      const start = input.selectionStart;
      const end = input.selectionEnd;
      const newLatex = currentStep.latex.substring(0, start) + sym.latex + currentStep.latex.substring(end);
      updateStep(activeStep, newLatex);
      setTimeout(() => {
        const newPos = start + sym.latex.length + (sym.cursor || 0);
        input.focus();
        input.setSelectionRange(newPos, newPos);
      }, 0);
    } else {
      updateStep(activeStep, currentStep.latex + sym.latex);
    }
  };

  const insertElement = (el) => {
    if (activeTab === "elements") {
      insertAtCursor(el.symbol);
    }
  };

  const clearAll = () => {
    setSteps([{ id: 1, latex: "", label: "Paso 1", mode: activeTab }]);
    setActiveStep(1);
  };

  const handleSubmit = () => {
    const content = steps
      .filter(s => s.latex.trim())
      .map(s => {
        const isChemMode = s.mode === "chem" || s.mode === "bio" || s.mode === "elements";
        const wrapped = isChemMode && !s.latex.includes("\\ce{") ? `\\ce{${s.latex}}` : s.latex;
        return `${s.label}: $${wrapped}$`;
      })
      .join("\n");
    if (content.trim()) {
      onSubmit(content, steps);
    }
  };

  const renderPreview = (step) => {
    if (!step.latex) return null;
    const isChemMode = step.mode === "chem" || step.mode === "bio" || step.mode === "elements";
    const latex = isChemMode && !step.latex.includes("\\ce{") && !step.latex.includes("\\frac") && !step.latex.includes("\\text") ? `\\ce{${step.latex}}` : step.latex;
    return (
      <div className="mt-3 p-3 bg-white rounded-lg border border-gray-100 min-h-[50px] flex items-center justify-center overflow-x-auto">
        <ErrorBoundary fallback={<span className="text-red-500 text-sm">Error en la formula</span>}>
          <BlockMath math={latex} />
        </ErrorBoundary>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden" data-testid="science-whiteboard">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between" style={{ backgroundColor: `${subjectColor}08` }}>
        <div className="flex items-center gap-2">
          <FlaskConical className="w-5 h-5" style={{ color: subjectColor }} />
          <span className="font-semibold text-gray-800">Pizarra Cientifica</span>
        </div>
        <Button variant="ghost" size="sm" onClick={clearAll} className="text-gray-500 hover:text-red-500">
          <RotateCcw className="w-4 h-4 mr-1" /> Limpiar
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-100">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => {
              setActiveTab(tab.id);
              setSteps(s => s.map(step => step.id === activeStep ? { ...step, mode: tab.id } : step));
            }}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium transition-all ${
              activeTab === tab.id
                ? "text-teal-700 border-b-2 border-teal-500 bg-teal-50/50"
                : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
            }`}
            data-testid={`tab-${tab.id}`}
          >
            <tab.icon className="w-3.5 h-3.5" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Symbol Toolbar */}
      <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 max-h-[160px] overflow-y-auto">
        {activeTab === "chem" && (
          <div className="space-y-2">
            <div className="flex flex-wrap gap-1.5">
              {chemCompounds.map((sym, i) => (
                <button key={sym.latex} onClick={() => insertSymbol(sym)} className="px-2.5 h-8 rounded-lg bg-white border border-gray-200 hover:border-teal-300 hover:bg-teal-50 text-xs font-medium text-gray-700 transition-all" data-testid={`chem-sym-${i}`}>
                  {sym.label}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap gap-1.5 pt-1 border-t border-gray-200">
              {chemReactionSymbols.map((sym) => (
                <button key={sym.latex} onClick={() => insertSymbol(sym)} className="px-2 h-7 rounded-md bg-white border border-gray-200 hover:border-teal-300 hover:bg-teal-50 text-xs font-medium text-gray-600 transition-all">
                  {sym.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {activeTab === "physics" && (
          <div className="space-y-2">
            <div className="flex flex-wrap gap-1.5">
              {physicsSymbols.slice(0, 12).map((sym, i) => (
                <button key={i} onClick={() => insertSymbol(sym)} className="px-2.5 h-8 rounded-lg bg-white border border-gray-200 hover:border-blue-300 hover:bg-blue-50 text-xs font-medium text-gray-700 transition-all">
                  {sym.label}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap gap-1.5 pt-1 border-t border-gray-200">
              {physicsSymbols.slice(12).map((sym, i) => (
                <button key={i} onClick={() => insertSymbol(sym)} className="w-8 h-8 rounded-lg bg-white border border-gray-200 hover:border-blue-300 hover:bg-blue-50 text-xs font-medium text-gray-700 transition-all flex items-center justify-center">
                  {sym.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {activeTab === "bio" && (
          <div className="flex flex-wrap gap-1.5">
            {bioSymbols.map((sym, i) => (
              <button key={i} onClick={() => insertSymbol(sym)} className="px-2.5 h-8 rounded-lg bg-white border border-gray-200 hover:border-green-300 hover:bg-green-50 text-xs font-medium text-gray-700 transition-all">
                {sym.label}
              </button>
            ))}
          </div>
        )}

        {activeTab === "elements" && (
          <div className="grid grid-cols-6 sm:grid-cols-8 gap-1">
            {periodicElements.map((el) => (
              <button
                key={el.symbol}
                onClick={() => insertElement(el)}
                className="flex flex-col items-center py-1.5 rounded-lg bg-white border border-gray-200 hover:border-teal-400 hover:bg-teal-50 transition-all"
                title={`${el.name} (Z=${el.z})`}
                data-testid={`element-${el.symbol}`}
              >
                <span className="text-[9px] text-gray-400">{el.z}</span>
                <span className="text-sm font-bold text-gray-800">{el.symbol}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Steps Area */}
      <div className="p-4 space-y-3 max-h-[250px] overflow-y-auto">
        {steps.map((step) => (
          <div key={step.id} className={`rounded-xl border-2 transition-all ${activeStep === step.id ? "border-teal-400 bg-teal-50/30" : "border-gray-100 bg-gray-50/50"}`}>
            <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100">
              <span className="text-xs font-semibold text-gray-500">{step.label}</span>
              {steps.length > 1 && (
                <button onClick={() => removeStep(step.id)} className="text-gray-400 hover:text-red-500 transition-colors">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <div className="p-3">
              <input
                ref={el => inputRefs.current[step.id] = el}
                type="text"
                value={step.latex}
                onChange={(e) => updateStep(step.id, e.target.value)}
                onFocus={() => setActiveStep(step.id)}
                placeholder={activeTab === "chem" ? "Ej: 2H2 + O2 -> 2H2O" : activeTab === "physics" ? "Ej: F = ma" : "Escribe tu formula..."}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent font-mono"
                data-testid={`step-input-${step.id}`}
              />
              {renderPreview(step)}
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="px-4 py-3 border-t border-gray-100 bg-gray-50 flex items-center justify-between">
        <Button variant="outline" size="sm" onClick={addStep} className="text-gray-600">
          <Plus className="w-4 h-4 mr-1" /> Agregar paso
        </Button>
        <Button onClick={handleSubmit} size="sm" className="text-white" style={{ backgroundColor: subjectColor }} disabled={!steps.some(s => s.latex.trim())} data-testid="science-submit-btn">
          <Send className="w-4 h-4 mr-1" /> Verificar con el tutor
        </Button>
      </div>
    </div>
  );
};

class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidUpdate(prevProps) { if (prevProps.children !== this.props.children) this.setState({ hasError: false }); }
  render() { return this.state.hasError ? this.props.fallback : this.props.children; }
}

export default ScienceWhiteboard;
